// client/src/components/event/VendorPanel.jsx
//
// Lightweight vendor tracker for one event. Table + add/edit form against
// /api/events/:tenant/:eventId/vendors.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const STATUSES = ['considering', 'contacted', 'booked', 'paid', 'cancelled'];
const EMPTY = {
  name: '', category: '', status: 'considering', quoted_amount: '',
  contact_name: '', contact_email: '', contact_phone: '', website: '', notes: '',
};

export default function VendorPanel({ tenantId, eventId }) {
  const base = `/api/events/${tenantId}/${eventId}/vendors`;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | {}(new) | {id,...}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(base);
      setVendors(data.vendors || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v, quoted_amount: v.quoted_amount ?? '' });
    setEditing(v);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = { ...form, quoted_amount: form.quoted_amount === '' ? null : Number(form.quoted_amount) };
      if (editing && editing.id) await axios.put(`${base}/${editing.id}`, body);
      else await axios.post(base, body);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${base}/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed');
    }
  };

  const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Vendors</h2>
        <button onClick={openNew} className="text-sm px-3 py-1.5 rounded bg-cobalt text-white">Add vendor</button>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-black/10 dark:border-white/10 opacity-70">
                <th className="p-2">Vendor</th>
                <th className="p-2">Category</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Quoted</th>
                <th className="p-2 text-right">Spent</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} className="border-b border-black/5 dark:border-white/5">
                  <td className="p-2">
                    <button onClick={() => openEdit(v)} className="font-medium underline-offset-2 hover:underline">{v.name}</button>
                    {v.contact_email && <div className="text-xs opacity-60">{v.contact_email}</div>}
                  </td>
                  <td className="p-2">{v.category || '—'}</td>
                  <td className="p-2 capitalize">{v.status}</td>
                  <td className="p-2 text-right">{money(v.quoted_amount)}</td>
                  <td className="p-2 text-right">{money(v.budget_actual)}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => remove(v.id)} className="text-xs text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center opacity-60">No vendors yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-md bg-white dark:bg-neutral-900 text-black dark:text-white rounded-xl p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold">{editing.id ? 'Edit vendor' : 'New vendor'}</h3>
            {[
              ['name', 'Name'], ['category', 'Category (catering, venue, AV…)'],
              ['contact_name', 'Contact name'], ['contact_email', 'Contact email'],
              ['contact_phone', 'Contact phone'], ['website', 'Website'],
            ].map(([k, label]) => (
              <input
                key={k} placeholder={label} value={form[k] || ''}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
              />
            ))}
            <div className="flex gap-2">
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="flex-1 px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm capitalize"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                type="number" step="0.01" placeholder="Quoted $" value={form.quoted_amount}
                onChange={(e) => setForm((f) => ({ ...f, quoted_amount: e.target.value }))}
                className="w-28 px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
              />
            </div>
            <textarea
              placeholder="Notes" value={form.notes || ''} rows={2}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15 text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded bg-cobalt text-white text-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
